import React from "react";
import {
  Box,
  Typography,
  Container,
  Stack,
  Link,
  useTheme,
  useMediaQuery,
  Divider,
  styled,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { BlagoomStandardLogo } from "../../components/icons/icons-svg/blagoom-standard-logo";
import { useHistory } from "react-router-dom";

export const CopyrightAboutUs = ({ className }) => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      className={className}
      sx={{
        backgroundColor: '#f8f9fa',
        mt: { xs: 6, md: 8 },
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
      }}
    >
      <Container 
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* Main Content */}
        <Box
          sx={{
            py: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'flex-start' },
            gap: { xs: 3, md: 4 },
          }}
        >
          {/* Logo and Copyright */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', sm: 'flex-start' },
              minWidth: { xs: '100%', sm: '300px' },
            }}
          >
            <Box 
              sx={{ 
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', sm: 'flex-start' },
                minHeight: '40px',
              }}
            >
              <StyledBlagoomStandardLogo color={theme.palette.grey[800]} />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.grey[600],
                fontSize: '14px',
                lineHeight: 1.6,
                textAlign: { xs: 'center', sm: 'left' },
                whiteSpace: 'pre-line',
              }}
            >
              <FormattedMessage id="footer.copyright" />
            </Typography>
          </Box>

          {/* Learn More Section */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', sm: 'flex-start' },
              height: '100%',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.grey[800],
                mb: 2,
                fontSize: '18px',
              }}
            >
              <FormattedMessage id="footer.learn-more" />
            </Typography>
            <Stack
              direction="column"
              spacing={1}
              sx={{
                alignItems: { xs: 'center', sm: 'flex-start' },
              }}
            >
              <Link
                component="button"
                variant="body2"
                onClick={() => history.push('/contact')}
                sx={{
                  color: theme.palette.grey[600],
                  textDecoration: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.primary.main,
                    textDecoration: 'underline',
                  },
                  transition: 'color 0.2s ease',
                }}
              >
                <FormattedMessage id="footer.about-us" />
              </Link>
              <Link
                component="button"
                variant="body2"
                onClick={() => history.push('/help')}
                sx={{
                  color: theme.palette.grey[600],
                  textDecoration: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.primary.main,
                    textDecoration: 'underline',
                  },
                  transition: 'color 0.2s ease',
                }}
              >
                <FormattedMessage id="footer.faq" />
              </Link>
            </Stack>
          </Box>
        </Box>

        <Divider sx={{ borderColor: theme.palette.grey[300] }} />

        {/* Bottom Links */}
        <Box
          sx={{
            py: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: { xs: 'center', sm: 'flex-start' },
            gap: { xs: 1, sm: 3 },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push('/agb')}
            sx={{
              color: theme.palette.grey[600],
              textDecoration: 'none',
              fontSize: '14px',
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.primary.main,
                textDecoration: 'underline',
              },
              transition: 'color 0.2s ease',
            }}
          >
            <FormattedMessage id="footer.general-terms" />
          </Link>
          <Box
            sx={{
              display: { xs: 'none', sm: 'block' },
              width: '1px',
              height: '16px',
              backgroundColor: theme.palette.grey[400],
            }}
          />
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push('/imprint')}
            sx={{
              color: theme.palette.grey[600],
              textDecoration: 'none',
              fontSize: '14px',
              cursor: 'pointer',
              '&:hover': {
                color: theme.palette.primary.main,
                textDecoration: 'underline',
              },
              transition: 'color 0.2s ease',
            }}
          >
            <FormattedMessage id="footer.imprint" />
          </Link>
        </Box>
      </Container>
    </Box>
  );
};

const StyledBlagoomStandardLogo = styled(BlagoomStandardLogo)(({ theme }) => ({
  margin: "20px 5px",
  width: "150px",
  [`@media only screen and (max-width: ${theme.firstBreakpoint}px)`]: {
    margin: "10px 5px",
    width: "90px",
  },
}));

